import type { FootballAdmissionPayload } from './contracts';

const FRAME_DEADLINE_MILLISECONDS = 2_000;
const HEARTBEAT_IDLE_MILLISECONDS = 5_000;
const REPLAY_FRAME_INTERVAL_MILLISECONDS = 50;
const RECONNECT_DELAYS_MILLISECONDS = [100, 250, 500, 1_000] as const;

type MovementFrame = Readonly<{
  sequence: number;
  posX: number;
  posY?: number;
  posZ: number;
  rotation: number;
  boostActive?: true;
}>;

type Pending = {
  movement: MovementFrame;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  cleanup: () => void;
  deadline?: ReturnType<typeof setTimeout>;
};

export type FootballRealtimeTelemetry = Readonly<{
  movementTimeouts: number;
  reconnects: number;
  recoveredFrames: number;
  inFlightHighWaterMark: number;
}>;

export class FootballRealtimeResponseError extends Error {
  override readonly name = 'FootballRealtimeResponseError';
  constructor(readonly response: unknown) {
    super('The football realtime server rejected the frame.');
  }
}

/** A reconnecting, admission-bounded transport for the 20 Hz football movement stream. */
export class FootballRealtimeClient {
  private socket: WebSocket | undefined;
  private ready?: Promise<void>;
  private readyResolve: (() => void) | undefined;
  private readyReject: ((reason?: unknown) => void) | undefined;
  private readonly pendingFrames = new Map<number, Pending>();
  private explicitlyClosed = false;
  private terminalFailure?: Error;
  private socketGeneration = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private replayTimer: ReturnType<typeof setTimeout> | undefined;
  private replayActive = false;
  private replayQueue: number[] = [];
  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private lastWriteAt = 0;
  private movementTimeouts = 0;
  private reconnects = 0;
  private recoveredFrames = 0;
  private inFlightHighWaterMark = 0;

  constructor(
    readonly token: string,
    private admission: FootballAdmissionPayload,
  ) {}

  get villageId(): number {
    return this.admission.villageId;
  }

  get available(): boolean {
    return (
      !this.explicitlyClosed &&
      this.terminalFailure === undefined &&
      Date.now() < this.admission.expiresAt
    );
  }

  get telemetry(): FootballRealtimeTelemetry {
    return {
      movementTimeouts: this.movementTimeouts,
      reconnects: this.reconnects,
      recoveredFrames: this.recoveredFrames,
      inFlightHighWaterMark: this.inFlightHighWaterMark,
    };
  }

  updateAdmission(admission: FootballAdmissionPayload): void {
    if (
      admission.ticket !== this.admission.ticket ||
      admission.sessionGeneration !== this.admission.sessionGeneration ||
      admission.villageId !== this.admission.villageId
    ) {
      this.close();
      return;
    }
    this.admission = {
      ...admission,
      expiresAt: Math.max(this.admission.expiresAt, admission.expiresAt),
    };
  }

  async connect(): Promise<void> {
    if (!this.available) {
      throw this.terminalFailure ?? new Error('The football realtime admission expired.');
    }
    if (this.socket?.readyState === WebSocket.OPEN) return;
    if (this.ready === undefined) {
      this.ready = new Promise<void>((resolve, reject) => {
        this.readyResolve = resolve;
        this.readyReject = reject;
      });
      void this.ready.catch(() => undefined);
      this.openSocket();
    }
    return this.ready;
  }

  async submitMovement(movement: MovementFrame, signal?: AbortSignal): Promise<unknown> {
    signal?.throwIfAborted();
    if (!this.available) throw new Error('The football realtime admission expired.');
    await this.connect();
    if (this.socket?.readyState !== WebSocket.OPEN) {
      throw new Error('The football realtime connection is unavailable.');
    }
    if (this.pendingFrames.size >= 4) {
      throw new Error('The football realtime movement pipeline is full.');
    }
    return new Promise<unknown>((resolve, reject) => {
      const aborted = (): void => {
        const pending = this.pendingFrames.get(movement.sequence);
        if (pending !== undefined) this.retirePending(movement.sequence, pending);
        reject(
          signal?.reason instanceof Error ? signal.reason : new Error('Football movement aborted.'),
        );
      };
      signal?.addEventListener('abort', aborted, { once: true });
      const pending: Pending = {
        movement,
        resolve,
        reject,
        cleanup: () => signal?.removeEventListener('abort', aborted),
      };
      this.pendingFrames.set(movement.sequence, pending);
      this.inFlightHighWaterMark = Math.max(this.inFlightHighWaterMark, this.pendingFrames.size);
      if (this.replayActive) {
        this.replayQueue.push(movement.sequence);
        this.replayQueue.sort((left, right) => left - right);
      } else {
        this.sendPending(pending);
      }
    });
  }

  close(): void {
    if (this.explicitlyClosed) return;
    this.explicitlyClosed = true;
    this.clearReconnectTimers();
    const socket = this.socket;
    this.socket = undefined;
    socket?.close(1000, 'football session ended');
    this.failPermanently(new Error('The football realtime connection was replaced.'));
  }

  private openSocket(): void {
    if (!this.available) {
      this.failPermanently(new Error('The football realtime admission expired.'));
      return;
    }
    const generation = ++this.socketGeneration;
    const url = new URL(this.admission.realtimePath, globalThis.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('ticket', this.admission.ticket);
    const socket = new WebSocket(url, this.admission.realtimeProtocol);
    this.socket = socket;
    let opened = false;
    const connectDeadline = setTimeout(() => {
      if (this.socket !== socket || generation !== this.socketGeneration || opened) return;
      this.movementTimeouts += 1;
      socket.close();
      this.disconnected(socket, new Error('The football realtime connect deadline expired.'));
    }, FRAME_DEADLINE_MILLISECONDS);
    socket.addEventListener('open', () => {
      if (this.socket !== socket || generation !== this.socketGeneration) return;
      opened = true;
      clearTimeout(connectDeadline);
      const recovered = this.reconnectAttempt > 0;
      this.reconnectAttempt = 0;
      if (recovered) this.reconnects += 1;
      this.readyResolve?.();
      this.readyResolve = undefined;
      this.readyReject = undefined;
      this.ready = Promise.resolve();
      this.lastWriteAt = Date.now();
      this.startHeartbeat();
      if (recovered) {
        this.replayActive = true;
        this.replayQueue = [...this.pendingFrames.keys()].sort((left, right) => left - right);
        this.drainReplayQueue(socket, generation);
      } else {
        for (const pending of this.pendingFrames.values()) this.sendPending(pending);
      }
    });
    socket.addEventListener('message', (event) => {
      if (this.socket === socket && generation === this.socketGeneration) this.receive(event.data);
    });
    socket.addEventListener('error', () => {
      clearTimeout(connectDeadline);
      this.disconnected(socket, new Error('The football realtime connection failed.'));
    });
    socket.addEventListener('close', () => {
      clearTimeout(connectDeadline);
      this.disconnected(socket, new Error('The football realtime connection closed.'));
    });
  }

  private sendPending(pending: Pending): void {
    const socket = this.socket;
    if (socket?.readyState !== WebSocket.OPEN) return;
    if (pending.deadline !== undefined) clearTimeout(pending.deadline);
    socket.send(
      JSON.stringify({
        type: 'frame',
        streamGeneration: this.admission.streamGeneration,
        sessionGeneration: this.admission.sessionGeneration,
        ...pending.movement,
      }),
    );
    this.lastWriteAt = Date.now();
    pending.deadline = setTimeout(() => {
      if (!this.pendingFrames.has(pending.movement.sequence)) return;
      this.movementTimeouts += 1;
      const active = this.socket;
      active?.close();
      if (active !== undefined) {
        this.disconnected(active, new Error('The football movement frame deadline expired.'));
      }
    }, FRAME_DEADLINE_MILLISECONDS);
  }

  private drainReplayQueue(socket: WebSocket, generation: number): void {
    if (
      !this.replayActive ||
      this.socket !== socket ||
      generation !== this.socketGeneration ||
      socket.readyState !== WebSocket.OPEN
    )
      return;
    let pending: Pending | undefined;
    while (pending === undefined && this.replayQueue.length > 0) {
      const sequence = this.replayQueue.shift();
      if (sequence !== undefined) pending = this.pendingFrames.get(sequence);
    }
    if (pending !== undefined) this.sendPending(pending);
    this.replayTimer = setTimeout(() => {
      this.replayTimer = undefined;
      if (this.replayQueue.length === 0) {
        this.replayActive = false;
        return;
      }
      this.drainReplayQueue(socket, generation);
    }, REPLAY_FRAME_INTERVAL_MILLISECONDS);
  }

  private receive(raw: unknown): void {
    if (typeof raw !== 'string') {
      this.disconnectCurrent(new Error('The football realtime server sent a non-JSON frame.'));
      return;
    }
    let frame: unknown;
    try {
      frame = JSON.parse(raw) as unknown;
    } catch (error) {
      this.disconnectCurrent(error);
      return;
    }
    if (!isRecord(frame)) {
      this.disconnectCurrent(new Error('The football realtime server sent an invalid frame.'));
      return;
    }
    if (frame.type === 'heartbeat-ack') {
      if (!this.validGeneration(frame)) {
        this.failPermanently(new Error('The football realtime generation changed.'));
        return;
      }
      if (typeof frame.expiresAt === 'number') {
        this.admission = { ...this.admission, expiresAt: frame.expiresAt };
      }
      return;
    }
    if (frame.type !== 'snapshot' && frame.type !== 'frame-error') {
      this.disconnectCurrent(new Error('The football realtime server sent an invalid frame.'));
      return;
    }
    if (!this.validGeneration(frame)) {
      this.failPermanently(new Error('The football realtime generation changed.'));
      return;
    }
    if (typeof frame.expiresAt === 'number') {
      this.admission = { ...this.admission, expiresAt: frame.expiresAt };
    }
    const responseSequence = frame.type === 'snapshot' ? frame.acceptedSequence : frame.sequence;
    if (typeof responseSequence !== 'number') {
      this.disconnectCurrent(new Error('The football snapshot omitted its sequence.'));
      return;
    }
    const acceptedSequence =
      typeof frame.acceptedSequence === 'number' ? frame.acceptedSequence : responseSequence;
    if (
      frame.type === 'frame-error' &&
      frame.code === 'stale_sequence' &&
      acceptedSequence >= responseSequence
    ) {
      this.resolveThrough(acceptedSequence, frame, true);
      return;
    }
    if (frame.type === 'frame-error') {
      const pending = this.pendingFrames.get(responseSequence);
      if (pending === undefined) return;
      this.retirePending(responseSequence, pending);
      pending.reject(new FootballRealtimeResponseError(frame));
      return;
    }
    this.resolveThrough(acceptedSequence, frame, false);
  }

  private resolveThrough(
    acceptedSequence: number,
    frame: Readonly<Record<string, unknown>>,
    recovered: boolean,
  ): void {
    const sequences = [...this.pendingFrames.keys()]
      .filter((sequence) => sequence <= acceptedSequence)
      .sort((left, right) => left - right);
    for (const sequence of sequences) {
      const pending = this.pendingFrames.get(sequence);
      if (pending === undefined) continue;
      this.retirePending(sequence, pending);
      if (recovered) this.recoveredFrames += 1;
      pending.resolve(
        this.movementResponse(frame, pending.movement, sequence, sequence === acceptedSequence),
      );
    }
  }

  private movementResponse(
    frame: Readonly<Record<string, unknown>>,
    movement: MovementFrame,
    sequence: number,
    includeContactResult: boolean,
  ): unknown {
    const authoritativePose =
      includeContactResult || !isRecord(frame.authoritativePose)
        ? frame.authoritativePose
        : {
            ...frame.authoritativePose,
            posX: movement.posX,
            ...(movement.posY === undefined ? {} : { posY: movement.posY }),
            posZ: movement.posZ,
            rotation: movement.rotation,
          };
    return {
      protocolVersion: '2',
      movementSubmission: {
        status: 'submitted',
        sequence,
        authoritativePose,
        footballContactAcknowledgements:
          includeContactResult && Array.isArray(frame.contactAcknowledgements)
            ? frame.contactAcknowledgements
            : [],
        ...(!includeContactResult || frame.ballUpdate === undefined
          ? {}
          : { ballUpdate: frame.ballUpdate }),
        ...(!includeContactResult || frame.participantPoses === undefined
          ? {}
          : { footballParticipantPoses: frame.participantPoses }),
        ...(!includeContactResult || frame.footballAdmission === undefined
          ? {}
          : { footballAdmission: frame.footballAdmission }),
        ...(!includeContactResult || frame.physicsLease === undefined
          ? {}
          : { physicsLease: frame.physicsLease }),
        ...(!includeContactResult || frame.footballContactBlocked === undefined
          ? {}
          : { footballContactBlocked: frame.footballContactBlocked }),
      },
    };
  }

  private retirePending(sequence: number, pending: Pending): void {
    this.pendingFrames.delete(sequence);
    if (pending.deadline !== undefined) clearTimeout(pending.deadline);
    pending.cleanup();
  }

  private validGeneration(frame: Readonly<Record<string, unknown>>): boolean {
    return (
      frame.streamGeneration === this.admission.streamGeneration &&
      frame.sessionGeneration === this.admission.sessionGeneration
    );
  }

  private disconnected(socket: WebSocket, reason: unknown): void {
    if (this.socket !== socket || this.explicitlyClosed || this.terminalFailure !== undefined)
      return;
    this.socket = undefined;
    this.stopHeartbeat();
    this.stopReplay();
    for (const pending of this.pendingFrames.values()) {
      if (pending.deadline !== undefined) clearTimeout(pending.deadline);
      delete pending.deadline;
    }
    if (this.readyResolve === undefined) {
      this.ready = new Promise<void>((resolve, reject) => {
        this.readyResolve = resolve;
        this.readyReject = reject;
      });
      void this.ready.catch(() => undefined);
    }
    this.scheduleReconnect(reason);
  }

  private disconnectCurrent(reason: unknown): void {
    const socket = this.socket;
    socket?.close();
    if (socket !== undefined) this.disconnected(socket, reason);
  }

  private scheduleReconnect(reason: unknown): void {
    const delay = RECONNECT_DELAYS_MILLISECONDS[this.reconnectAttempt];
    if (delay === undefined || Date.now() + delay >= this.admission.expiresAt) {
      this.failPermanently(reason);
      return;
    }
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const socket = this.socket;
      if (
        socket?.readyState !== WebSocket.OPEN ||
        Date.now() - this.lastWriteAt < HEARTBEAT_IDLE_MILLISECONDS
      )
        return;
      socket.send(
        JSON.stringify({
          type: 'heartbeat',
          streamGeneration: this.admission.streamGeneration,
          sessionGeneration: this.admission.sessionGeneration,
        }),
      );
      this.lastWriteAt = Date.now();
    }, 1_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== undefined) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
  }

  private clearReconnectTimers(): void {
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopReplay();
    this.stopHeartbeat();
  }

  private stopReplay(): void {
    if (this.replayTimer !== undefined) clearTimeout(this.replayTimer);
    this.replayTimer = undefined;
    this.replayActive = false;
    this.replayQueue = [];
  }

  private failPermanently(reason: unknown): void {
    const error =
      reason instanceof Error
        ? reason
        : new Error('The football realtime connection failed.', { cause: reason });
    this.terminalFailure ??= error;
    this.clearReconnectTimers();
    this.readyReject?.(this.terminalFailure);
    this.readyResolve = undefined;
    this.readyReject = undefined;
    for (const [sequence, pending] of this.pendingFrames) {
      this.retirePending(sequence, pending);
      pending.reject(this.terminalFailure);
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
